<template>
    <template v-if="page">
        <div class="min-h-dvh radial-grad tw-relative tw-flex tw-flex-col tw-overflow-hidden">
            <div class="io-container tw-relative tw-z-20 tw-my-0 tw-flex tw-flex-grow tw-flex-col tw-items-center tw-justify-start tw-py-8 tw-pb-0 tw-text-center 3xl:tw-pt-16">
                <Tag
                    :color="page.color"
                    :text="page.name" />
                <h1
                    class="tw-mt-10"
                    v-html="page.bannerTitle"></h1>
                <div
                    class="h3 ckeditor tw-mt-3 md:tw-mt-6 lg:tw-mt-8"
                    v-html="page.bannerDescription"></div>
                <div
                    v-if="page.bannerImage || page.bannerImageMobile"
                    class="banner-img tw-relative tw-mt-16 tw-w-full">
                    <img
                        v-if="page.bannerImage"
                        alt=""
                        class="tw-relative tw-z-10 max-lg:tw-hidden"
                        :src="useMedia(page.bannerImage).original" />
                    <img
                        v-if="page.bannerImageMobile"
                        alt=""
                        class="tw-relative tw-z-10 lg:tw-hidden"
                        :src="useMedia(page.bannerImageMobile).original" />
                </div>
            </div>
        </div>
        <div class="excellence tw-relative">
            <div class="io-container tw-mt-0 tw-py-16">
                <h2
                    class=""
                    v-html="page.excellenceTitle"></h2>
                <div class="tw-grid tw-grid-cols-1 tw-gap-8 xl:tw-grid-cols-2 2xl:tw-grid-cols-3">
                    <div
                        v-for="excellence in page.excellenceBases"
                        :key="excellence.id"
                        class="tw-relative tw-flex tw-flex-col tw-gap-4">
                        <div class="tw-flex tw-items-center tw-justify-start tw-gap-5">
                            <div class="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-xl tw-bg-blue-40 tw-p-3">
                                <img
                                    alt=""
                                    :src="useMedia(excellence.icon).small" />
                            </div>
                            <h3 class="tw-font-bold">
                                {{ excellence.title }}
                            </h3>
                        </div>
                        <h3
                            class="tw-font-light"
                            v-html="excellence.description"></h3>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="timelineItems.length"
            class="io-container tw-text-center">
            <h2
                class=""
                v-html="page.historyTitle"></h2>
            <h3
                v-if="page.historyDescription"
                class="ckeditor tw-mb-16"
                v-html="page.historyDescription"></h3>
            <v-slide-group
                ref="timeline"
                v-model="activeStep"
                center-active
                class="pa-4"
                show-arrows="always">
                <v-slide-group-item
                    v-for="(item, index) in timelineItems"
                    :key="item.id"
                    v-slot="{ isSelected }">
                    <div
                        class="timeline-item tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2"
                        :class="`timeline-item-${index}`">
                        <v-btn
                            class="btn-timeline tw-relative"
                            :color="activeStep >= index ? 'primary' : 'gray'"
                            icon
                            size="small"
                            variant="outlined"
                            @click="activeStep = index">
                            <v-icon
                                v-if="isSelected"
                                size="x-large"
                                >mdi-circle</v-icon
                            >
                        </v-btn>
                        <p
                            class="tw-w-28 tw-text-sm tw-font-medium"
                            :class="{ 'tw-text-gray-700': !isSelected, 'tw-text-primary': isSelected }">
                            {{ formatDate(item.date) }}
                        </p>
                    </div>
                </v-slide-group-item>
                <template #next>
                    <v-btn
                        color="primary"
                        icon
                        size="small"
                        variant="outlined"
                        @click.prevent.stop="onNext">
                        <v-icon>mdi-arrow-right</v-icon>
                    </v-btn>
                </template>
                <template #prev>
                    <v-btn
                        color="primary"
                        icon
                        size="small"
                        variant="outlined"
                        @click.prevent.stop="onPrev">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                </template>
            </v-slide-group>

            <article class="tw-mx-auto tw-mt-8 tw-flex tw-max-w-3xl tw-flex-col tw-items-center tw-justify-center tw-gap-6">
                <div
                    v-if="timelineItems[activeStep].title"
                    class="tw-text-base tw-font-bold lg:tw-text-xl"
                    v-html="timelineItems[activeStep].title"></div>
                <v-divider
                    v-if="timelineItems[activeStep].title && timelineItems[activeStep].event"
                    length="100"></v-divider>
                <div
                    v-if="timelineItems[activeStep].event"
                    class="tw-text-sm tw-font-medium lg:tw-text-base"
                    v-html="timelineItems[activeStep].event"></div>
            </article>
        </div>
        <div
            v-if="page.responsabilities?.length > 0"
            class="io-container tw-pt-8">
            <h2
                class=""
                v-html="page.responsabilityTitle"></h2>
            <ResponsabilitiesSlider :responsabilities="page.responsabilities" />
        </div>
    </template>
    <template v-else>
        <!-- loader -->
        <Loader />
    </template>
</template>

<script setup lang="ts">
    import Loader from '@/components/Loader.vue';
    import { useMedia } from '@/composables/useMedia';
    import { useAppStore } from '@/store/app';
    import { VSlideGroup } from 'vuetify/components';
    import ResponsabilitiesSlider from '@/components/ResponsabilitiesSlider.vue';
    import moment from 'moment';

    const appStore = useAppStore();

    const activeStep = ref(0);

    const timeline = useTemplateRef<VSlideGroup>('timeline');

    const timelineItems = computed(() => {
        const t = appStore.getOurIdentity?.timeline || [];
        return t;
    });

    const page = computed(() => {
        return appStore.getOurIdentity;
    });

    const formatDate = (date: string) => {
        return moment(date).format('DD MMM YYYY').toUpperCase();
    };

    function onNext() {
        if (activeStep.value < timelineItems.value.length - 1) {
            activeStep.value++;
        }
    }

    function onPrev() {
        if (activeStep.value > 0) {
            activeStep.value--;
        }
    }

    watchEffect(() => {
        if (page.value?.name) {
            document.title = page.value?.name + ' - IOTech';
        }
    });
</script>

<style scoped>
    .bg-gradient {
        position: relative;
        background-image: url('@/assets/gradients/gradient-blue-110.png');
        background-size: cover;
    }

    .banner-img > * {
        z-index: 2;
        position: relative;
    }

    .banner-img::before,
    .banner-img::after {
        z-index: 1;
        content: '';
        position: absolute;
        left: -25%;
        top: 0;
        right: -25%;
        bottom: 0;
        transform: translateY(50%);
        background-color: rgb(var(--v-theme-blue-30));
        background-size: cover;
    }

    .banner-img::after {
        transform: translateY(75%);
        bottom: -99%;
    }

    .excellence > * {
        z-index: 4;
        position: relative;
    }

    .excellence::before {
        z-index: 1;
        content: '';
        position: absolute;
        left: -25%;
        top: 0;
        right: -25%;
        bottom: 0;
        /* transform: translateY(-25%); */
        background-color: rgb(var(--v-theme-blue-30));
        background-size: cover;
    }
</style>

<style>
    @keyframes scroll {
        0% {
            transform: translateX(50%);
        }
        100% {
            transform: translateX(-100%);
        }
    }

    .timeline-item {
        margin-left: 76px;
    }

    .timeline-item:last-child {
        margin-right: 76px;
    }

    .btn-timeline {
        /* add a var for the width of the line */
        --line-width: 150px;
        z-index: 2;
    }

    /* add a horizontal line to the left of the button */
    .btn-timeline::before {
        content: '';
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        left: calc(var(--line-width) * -1);
        width: var(--line-width);
        height: 1px;
        background-color: currentColor;
        opacity: 1;
        z-index: 1;
    }
    /* if its the last .btn-time */

    /* .timeline-item:last-child .btn-timeline::after {
        content: '';
        position: absolute;
        top: 50%;
        transform: translateY(-50%) translateX(100%);
        right: 0px;
        width: var(--line-width);
        height: 1px;
        background-color: currentColor;
        opacity: 1;
        z-index: 1;
        border-radius: 0;
        border: unset;
        left: unset;
    } */

    .v-slide-group__content {
        justify-content: center;
    }

    .v-slide-group__next--disabled,
    .v-slide-group__prev--disabled {
        opacity: 1 !important;
        pointer-events: all !important;
    }

    .v-slide-group__next,
    .v-slide-group__prev {
        align-items: flex-start;
    }
    .v-slide-group__next {
        justify-content: flex-start;
    }
    .v-slide-group__prev {
        justify-content: flex-end;
    }
</style>
